// 📂 SelectedProvider.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { storage } from "../Services/storage.service";
import { storageKeys } from "../Constants/storage.key";

const SelectedContext = createContext();

export const useSelected = () => {
  const context = useContext(SelectedContext);
  if (!context) {
    throw new Error("useSelected must be used within a SelectedProvider");
  }
  return context;
};

export const SelectedProvider = ({ children }) => {
  const defaultCharts = [
    "table-drivers",
    "vehiclesCirculationBar",
    "vehiclesIdlenessStacked",
    "offHoursStacked",
    "offHoursLine",
    "speedLimit",
    "table-multas"
  ];

  const [isInitialized, setIsInitialized] = useState(false);

  const [currentUserId, setCurrentUserId] = useState(() => {
    const userSessionData = storage.get(storageKeys.settings);
    return userSessionData?.userId || 'anonymous';
  });

  const [selectedValues, setSelectedValues] = useState(() => {
    const userSessionData = storage.get(storageKeys.settings);
    const userId = userSessionData?.userId || 'anonymous';

    try {
      const saved = Cookies.get(`selectedFilters_${userId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          grafico: parsed.grafico?.length ? parsed.grafico : defaultCharts,
          periodo: parsed.periodo || [],
          veiculos: parsed.veiculos || [],
          veiculosGroups: parsed.veiculosGroups || [],
          motoristas: parsed.motoristas || [],
          velocidade: parsed.velocidade || [],
          tempominimo: parsed.tempominimo || [],
          tipoevento: parsed.tipoevento || [],
          grupo: parsed.grupo?.length ? parsed.grupo : [0],
        };
      }
    } catch (err) {
      // Erro silencioso
    }
    return {
      grafico: defaultCharts,
      periodo: [],
      veiculos: [],
      veiculosGroups: [],
      motoristas: [],
      velocidade: [],
      tempominimo: [],
      tipoevento: [],
      grupo: [0],
    };
  });

  // Marca como inicializado após o primeiro render
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Monitora mudanças no usuário logado
  useEffect(() => {
    const checkUserChange = () => {
      const userSessionData = storage.get(storageKeys.settings);
      const newUserId = userSessionData?.userId || 'anonymous';

      if (newUserId !== currentUserId) {
        setCurrentUserId(newUserId);

        try {
          const saved = Cookies.get(`selectedFilters_${newUserId}`);
          if (saved) {
            const parsed = JSON.parse(saved);
            setSelectedValues({
              grafico: parsed.grafico?.length ? parsed.grafico : defaultCharts,
              periodo: parsed.periodo || [],
              veiculos: parsed.veiculos || [],
              veiculosGroups: parsed.veiculosGroups?.length ? parsed.veiculosGroups : [],
              motoristas: parsed.motoristas || [],
              velocidade: parsed.velocidade || [],
              tempominimo: parsed.tempominimo || [],
              tipoevento: parsed.tipoevento || [],
              grupo: parsed.grupo?.length ? parsed.grupo : [0],
            });
          } else {
            setSelectedValues({
              grafico: defaultCharts,
              periodo: [],
              veiculos: [],
              veiculosGroups: [],
              motoristas: [],
              velocidade: [],
              tempominimo: [],
              tipoevento: [],
              grupo: [0],
            });
          }
        } catch (err) {
          // Erro silencioso
        }
      }
    };

    const interval = setInterval(checkUserChange, 5000);
    return () => clearInterval(interval);
  }, [currentUserId]);

  // Salva automaticamente no cookie por usuário
  useEffect(() => {
    if (!isInitialized) return;
    
    Cookies.set(`selectedFilters_${currentUserId}`, JSON.stringify(selectedValues), {
      expires: 7,
    });
  }, [selectedValues, currentUserId, isInitialized]);

  // Função genérica para atualizar qualquer filtro
  const toggleOption = (filterId, value, isMulti = true) => {
    setSelectedValues((prev) => {
      const current = prev[filterId] || [];
      let updated;

      if (!isMulti) {
        updated = [value];
      } else {
        updated = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value];
      }

      // 👇 Regra especial para grupo e veiculos
      // Aceita ID como valor (não espera mais índice)
      if (filterId === "grupo" || filterId === "veiculos") {
        if (value === 0 || value === "0") {
          // Se escolher TODOS (ID 0), reseta só pra TODOS
          updated = [0];
        } else {
          // Remove TODOS (ID 0) se escolher outro
          updated = updated.filter((v) => v !== 0 && v !== "0");
          if (updated.length === 0) {
            // Se ficar vazio, volta pra TODOS
            updated = [0];
          }
        }
      }

      const newState = { ...prev, [filterId]: updated };
      
      // Salva manualmente no cookie
      Cookies.set(`selectedFilters_${currentUserId}`, JSON.stringify(newState), {
        expires: 7,
      });
      
      return newState;
    });
  };

  return (
    <SelectedContext.Provider
      value={{
        selectedValues,
        setSelectedValues,
        toggleOption,
      }}
    >
      {children}
    </SelectedContext.Provider>
  );
};
